import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import './PublicMemorialPage.css';

/**
 * PUBLIC MEMORIAL PAGE - v4
 * Underline tabs, video lightbox support, better fonts
 */

const PublicMemorialPage = () => {
    const { code } = useParams();
    const [memorial, setMemorial] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [activeTab, setActiveTab] = useState('biography');
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    useEffect(() => {
        const fetchMemorial = async () => {
            try {
                setLoading(true);
                const response = await axios.get(`https://luminaback-production-5bb5.up.railway.app/q/${code}`);
                setMemorial(response.data);
            } catch (err) {
                console.error('Error fetching memorial:', err);
                setError('Gedenkseite wurde nicht gefunden oder ist nicht verfügbar');
            } finally {
                setLoading(false);
            }
        };

        fetchMemorial();
    }, [code]);

    const nextImage = () => {
        if (memorial?.mediaUrls && memorial.mediaUrls.length > 0) {
            setCurrentImageIndex((prev) =>
                prev === memorial.mediaUrls.length - 1 ? 0 : prev + 1
            );
        }
    };

    const prevImage = () => {
        if (memorial?.mediaUrls && memorial.mediaUrls.length > 0) {
            setCurrentImageIndex((prev) =>
                prev === 0 ? memorial.mediaUrls.length - 1 : prev - 1
            );
        }
    };

    const openLightbox = (index) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    const closeLightbox = () => {
        setLightboxOpen(false);
    };

    const nextLightboxImage = () => {
        if (memorial?.mediaUrls) {
            setLightboxIndex((prev) =>
                prev === memorial.mediaUrls.length - 1 ? 0 : prev + 1
            );
        }
    };

    const prevLightboxImage = () => {
        if (memorial?.mediaUrls) {
            setLightboxIndex((prev) =>
                prev === 0 ? memorial.mediaUrls.length - 1 : prev - 1
            );
        }
    };

    const getTemplateClass = () => {
        if (!memorial?.templateType) return 'gold-classic';

        const templateMap = {
            'GOLD_CLASSIC': 'gold-classic',
            'SILVER_ELEGANCE': 'silver-elegance',
            'PURE_WHITE': 'pure-white',
            'WARM_BEIGE': 'warm-beige',
            'DEEP_BLACK': 'deep-black',
            'ICE_BLUE': 'ice-blue'
        };

        return templateMap[memorial.templateType] || 'gold-classic';
    };

    const isVideo = (url) => {
        return url?.match(/\.(mp4|webm|ogg|mov)$/i);
    };

    if (loading) {
        return (
            <div className={`memorial-page-wrapper`}>
                <div className="loading-screen">
                    <div className="spinner"></div>
                    <p>Gedenkseite wird geladen…</p>
                </div>
            </div>
        );
    }

    if (error || !memorial) {
        return (
            <div className={`memorial-page-wrapper`}>
                <div className="error-screen">
                    <h2>Gedenkseite nicht gefunden</h2>
                    <p>{error || 'Diese Gedenkseite ist nicht verfügbar'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`memorial-page-wrapper ${getTemplateClass()}`}>
            <div className="memorial-content">
                {/* Memorial Card */}
                <motion.div
                    className="memorial-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    {/* Full Image/Video Section */}
                    <div className="media-section">
                        {memorial.mediaUrls && memorial.mediaUrls.length > 0 ? (
                            <>
                                {isVideo(memorial.mediaUrls[currentImageIndex]) ? (
                                    <video
                                        src={memorial.mediaUrls[currentImageIndex]}
                                        className="media-display"
                                        controls
                                        autoPlay
                                        loop
                                        muted
                                    />
                                ) : (
                                    <img
                                        src={memorial.mediaUrls[currentImageIndex]}
                                        alt={`${memorial.firstName} ${memorial.lastName}`}
                                        className="media-display"
                                    />
                                )}

                                {memorial.mediaUrls.length > 1 && (
                                    <div className="media-controls">
                                        <button onClick={prevImage} className="media-btn">‹</button>
                                        <span className="media-counter">
                                            {currentImageIndex + 1}/{memorial.mediaUrls.length}
                                        </span>
                                        <button onClick={nextImage} className="media-btn">›</button>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="empty-media">
                                <span className="empty-icon">🖼️</span>
                            </div>
                        )}
                    </div>

                    {/* Name Card */}
                    <div className="name-card">
                        <h1 className="memorial-fullname">
                            {memorial.firstName} {memorial.lastName}
                        </h1>
                        <p className="memorial-dates">
                            {memorial.yearOfBirth} — {memorial.yearOfDeath}
                        </p>

                        {/* Navigation Tabs */}
                        <div className="nav-tabs">
                            <button
                                className={`tab-btn ${activeTab === 'biography' ? 'active' : ''}`}
                                onClick={() => setActiveTab('biography')}
                            >
                                Biografie
                            </button>
                            {memorial.mediaUrls && memorial.mediaUrls.length > 0 && (
                                <button
                                    className={`tab-btn ${activeTab === 'gallery' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('gallery')}
                                >
                                    Galerie
                                </button>
                            )}
                            {memorial.location && (
                                <button
                                    className={`tab-btn ${activeTab === 'location' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('location')}
                                >
                                    Ort
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Content Section */}
                    <div className="content-section">
                        {activeTab === 'biography' && (
                            <div className="biography-content">
                                {memorial.lifeStory ? (
                                    <p className="life-story">{memorial.lifeStory}</p>
                                ) : (
                                    <p className="no-content">Keine Biografie verfügbar</p>
                                )}
                            </div>
                        )}

                        {activeTab === 'gallery' && memorial.mediaUrls && (
                            <div className="gallery-grid">
                                {memorial.mediaUrls.map((url, index) => (
                                    <div
                                        key={index}
                                        className={`gallery-thumb ${index === currentImageIndex ? 'selected' : ''}`}
                                        onClick={() => {
                                            setCurrentImageIndex(index);
                                            openLightbox(index);
                                        }}
                                    >
                                        {isVideo(url) ? (
                                            <video src={url} className="thumb-media" />
                                        ) : (
                                            <img src={url} alt={`Erinnerung ${index + 1}`} className="thumb-media" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {activeTab === 'location' && memorial.location && (
                            <div className="location-content">
                                <p className="location-text">📍 {memorial.location}</p>
                            </div>
                        )}
                    </div>
                </motion.div>

                {/* Bottom text */}
                <motion.p
                    className="memorial-tagline"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.8 }}
                >
                    In liebevoller Erinnerung — für immer in unseren Herzen
                </motion.p>
            </div>

            {/* Lightbox Modal - Now supports both images and videos */}
            <AnimatePresence>
                {lightboxOpen && memorial.mediaUrls && (
                    <motion.div
                        className="lightbox-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={closeLightbox}
                    >
                        <button className="lightbox-close" onClick={closeLightbox}>×</button>

                        <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                            {isVideo(memorial.mediaUrls[lightboxIndex]) ? (
                                <video
                                    src={memorial.mediaUrls[lightboxIndex]}
                                    className="lightbox-video"
                                    controls
                                    autoPlay
                                />
                            ) : (
                                <img
                                    src={memorial.mediaUrls[lightboxIndex]}
                                    alt={`Erinnerung ${lightboxIndex + 1}`}
                                    className="lightbox-image"
                                />
                            )}

                            {memorial.mediaUrls.length > 1 && (
                                <>
                                    <button className="lightbox-nav prev" onClick={prevLightboxImage}>‹</button>
                                    <button className="lightbox-nav next" onClick={nextLightboxImage}>›</button>
                                    <div className="lightbox-counter">
                                        {lightboxIndex + 1} / {memorial.mediaUrls.length}
                                    </div>
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default PublicMemorialPage;
